import React from 'react';
import {ITaskSingleVoyage} from "@containers/singleVoyage/tasks/types";
import ActionUiHandler from "@ui/template/actionUiHandler/ActionUiHandler";
import {SxType} from "@baseTypes/sx/sxTypes";
import ButtonCustom from "@ui/buttons/buttonCustom/ButtonCustom";
import {decisionSx} from "@containers/singleVoyage/tasks/taskActions/sx";
import {useTranslation} from "react-i18next";
import {useCloseTaskSingleVoyageQuery} from "@containers/singleVoyage/tasks/queryHooks/useTasksSingleVoyageQuery";
import {countOpenExecutionsTaskSingleVoyage} from "@containers/singleVoyage/tasks/helpers/executions";
import Typography from "@mui/material/Typography";

const sx: SxType = {
    root: {
        padding: '0',
        display: 'flex',
        gap: '1px',
        flexDirection: 'column',
    },
    hint: {
        fontSize: '11px',
        lineHeight: '15px',
        opacity: 0.65,
        padding: '0 10px 5px',
    }
}

interface IProps {
    task: ITaskSingleVoyage
    accountId: string | number
    voyageId: number
    closeModalFn?: () => void
    closePopoverFn?: () => void
}

/**
 * Closes every execution of the task that is still open. Already closed ones
 * are left alone - that is the difference from the old behaviour, where the
 * parent status was written over every subtask indiscriminately.
 */
const CloseTaskSingleVoyage = ({
                                   task,
                                   accountId,
                                   voyageId,
                                   closeModalFn,
                                   closePopoverFn,
                               }: IProps) => {

    const {t} = useTranslation()

    const {open, total} = countOpenExecutionsTaskSingleVoyage(task)

    const {
        isPending,
        mutate: close
    } = useCloseTaskSingleVoyageQuery({
        onSuccess: () => {
            closeModalFn && closeModalFn()
            closePopoverFn && closePopoverFn()
        }
    })

    const onClose = (resolution: 'COMPLETED' | 'CANCELLED') => {
        close({
            accountId: Number(accountId),
            voyageId,
            taskId: task.id,
            resolution,
        })
    }

    if (open === 0) {
        return (
            <ActionUiHandler sx={sx.root}>
                <Typography sx={sx.hint}>
                    {t('singleVoyage.closeTaskNothingToClose')}
                </Typography>
            </ActionUiHandler>
        )
    }

    return (
        <ActionUiHandler
            sx={sx.root}
            isLoading={isPending}
        >
            <Typography sx={sx.hint}>
                {t('singleVoyage.closeTaskConfirm', {open, total})}
            </Typography>

            <ButtonCustom
                fullWidth
                variant={'outlined'}
                sx={decisionSx.done}
                onClick={() => onClose('COMPLETED')}
                disabled={isPending}
            >
                {t('singleVoyage.closeTaskAsCompleted')}
            </ButtonCustom>

            <ButtonCustom
                fullWidth
                variant={'outlined'}
                sx={decisionSx.failed}
                onClick={() => onClose('CANCELLED')}
                disabled={isPending}
            >
                {t('singleVoyage.closeTaskAsCancelled')}
            </ButtonCustom>
        </ActionUiHandler>
    );
};

export default CloseTaskSingleVoyage;
